<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue'
  import { useRouter } from 'vue-router'
  import { FormsService, OrganisationsService } from '@/types/generated/services.gen'
  import {
    type SuperUserForm as FormType,
    type PaginatedTOrganisationList as PaginatedOrganisationList,
    type PatchedSuperUserFormRequest as PatchedFormRequest
  } from '@/types/generated/types.gen'
  import { ROUTES } from '@/router/routes'
  import { genericErrorToast, saveSuccessToast } from '@/lib/toasts'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { DownloadIcon, Trash2Icon } from 'lucide-vue-next'
  import { useTranslation } from '@/composables/useTranslation'
  import * as yup from 'yup'
  import { $SuperUserForm as $Form } from '@/types/generated'

  const router = useRouter()
  const { t } = useTranslation({
    en: {
      name: 'Name',
      description: 'Description',
      customer: 'Customer',
      document: 'Document'
    },
    de: {
      name: 'Name',
      description: 'Beschreibung',
      customer: 'Kunde',
      document: 'Dokument'
    }
  })

  const form = ref<FormType | null>(null)
  const organisationsList = ref<PaginatedOrganisationList | null>(null)
  const showDeleteModal = ref(false)

  const organisationOptions = computed(() => {
    return (
      organisationsList.value?.results.map((organisation) => ({
        value: organisation.uuid,
        title: organisation.name
      })) ?? []
    )
  })

  onMounted(async () => {
    try {
      organisationsList.value = await OrganisationsService.organisationsList({
        limit: 10000,
        offset: 0
      })
      form.value = (await FormsService.formsRetrieve({
        id: router.currentRoute.value.params.uuid.toString()
      })) as FormType
    } catch (error) {
      genericErrorToast()
    }
  })

  const handleSave = async (values: any) => {
    try {
      await FormsService.formsPartialUpdate({
        id: form.value!.id,
        formData: values as PatchedFormRequest
      })
      saveSuccessToast()
    } catch (error) {
      genericErrorToast()
    }
  }

  const handleDownload = async () => {
    try {
      if (!form.value?.file) {
        throw new Error('No file available for download')
      }

      const response = await fetch(form.value.file)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${form.value.title}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      genericErrorToast()
    }
  }

  const openDeleteModal = () => {
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
  }

  const confirmDelete = async () => {
    try {
      if (form.value) {
        await FormsService.formsDestroy({
          id: form.value.id
        })
        saveSuccessToast()
        router.push(ROUTES.ADMIN.DOCUMENTS.DOCUMENTS_LIST)
      }
    } catch (error) {
      genericErrorToast()
    } finally {
      closeDeleteModal()
    }
  }
</script>

<template>
  <div
    v-if="form"
    class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5"
  >
    <!-- Page Header -->
    <div class="flex items-center justify-between gap-x-5">
      <Breadcrumbs
        :items="[
          { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
          { title: 'Documents', to: ROUTES.ADMIN.DOCUMENTS.DOCUMENTS_LIST },
          {
            title: `Edit ${form.id}`,
            to: ROUTES.ADMIN.DOCUMENTS.DOCUMENT_DETAIL.replace(':uuid', form.id)
          }
        ]"
      />

      <div class="flex items-center justify-end gap-x-3">
        <Button
          variant="outline"
          class="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-black shadow-md
            hover:bg-gray-100 hover:text-gray-900"
          @click="handleDownload"
        >
          <div class="flex items-center">
            <DownloadIcon class="mr-2 h-5 w-5" />
            <span>Download</span>
          </div>
        </Button>
        <Button
          variant="outline"
          class="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-black shadow-md
            hover:bg-red-50 hover:text-red-900"
          @click="openDeleteModal"
        >
          <div class="flex items-center">
            <Trash2Icon class="mr-2 h-5 w-5" />
            <span>Delete</span>
          </div>
        </Button>
      </div>
    </div>

    <div
      class="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-700
        dark:bg-neutral-800"
    >
      <div class="w-full md:w-1/2">
        <AutoSavingForm @save="handleSave">
          <InputField
            v-model="form.title"
            :label="t('name')"
            name="title"
            :rules="yup.string().max($Form.properties.title.maxLength).required()"
          />
          <TextAreaField
            v-model="form.description"
            :label="t('description')"
            name="description"
            :rules="yup.string().optional()"
          />
          <SelectField
            v-model="form.organisation_uuid"
            :label="t('customer')"
            :is-filterable="true"
            name="organisation_uuid"
            :rules="yup.string().uuid().required()"
            :options="organisationOptions"
          />
        </AutoSavingForm>
        <label class="mb-2 block text-sm font-medium text-gray-800">{{ t('document') }}</label>
        <p class="ps-2">
          <a
            class="text-blue-500"
            target="_blank"
            :href="form.file!"
          >
            {{ form.file }}
          </a>
        </p>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <Dialog
      :open="showDeleteModal"
      @update:open="closeDeleteModal"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the document "{{ form.title }}"? This action cannot be
            undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            @click="closeDeleteModal"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            @click="confirmDelete"
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
